import React, { useState } from "react";
import {
  SwipeableDrawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  AccountBalanceWallet as WalletIcon,
  SwapHoriz as SwapIcon,
  Send as SendIcon,
  Receipt as HistoryIcon,
  QrCode as QrCodeIcon,
  Security as SecurityIcon,
  ArrowBack as ArrowBackIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  VolunteerActivism as DonationsIcon,
  CreditCard as CardIcon,
  AccountBalance as FiatIcon,
  Loyalty as RewardsIcon,
} from "@mui/icons-material";
import { shortenAddress } from "../utils/addressUtils";
import { useAppSelector } from "../hooks/hooks";
import SendSection from "./wallet/SendSection";
import ReceiveSection from "./wallet/ReceiveSection";
import HistorySection from "./wallet/HistorySection ";
import { useAppDispatch } from "../hooks/hooks";
import { fetchTransactions } from "../redux/walletSlice";
import CardsSection from "./wallet/CardsSection";
import WalletCreator from "./wallet/WalletCreator";
import { setWalletSection } from "../redux/uiSlice";
import { setWalletAddress } from "../redux/userSlice";

interface WalletDialogProps {
  open: boolean;
  onClose: () => void;
  onDisconnect: () => void;
}

const WalletDialog: React.FC<WalletDialogProps> = ({
  open,
  onClose,
  onDisconnect,
}) => {
  //const [activeSection, setActiveSection] = useState<WalletSection>("main");
  const [copied, setCopied] = useState(false);
  const [balanceType, setBalanceType] = useState<BalanceType>("crypto");
  const { walletSection: activeSection } = useAppSelector((state) => state.ui);

  const { balance } = useAppSelector((state) => state.wallet);
  const { user } = useAppSelector((state) => state.user);
  const { chainType } = useAppSelector((state) => state.wallet);
  const dispatch = useAppDispatch();

  // Mock balances for different types
  const balances = {
    crypto: {
      value: balance || "0",
      currency: chainType === "evm" ? "NDEIPI" : "MOVEUSD",
    },
    bank: { value: "1,245.67", currency: "USD" },
    rewards: { value: "5,250", currency: "Points" },
  };

  const handleCopyAddress = () => {
    if (!user) return;
    navigator.clipboard.writeText(user?.walletAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleBalanceTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newBalanceType: BalanceType | null
  ) => {
    if (newBalanceType !== null) {
      setBalanceType(newBalanceType);
    }
  };

  const renderBalance = () => {
    const currentBalance = balances[balanceType];
    return (
      <Typography variant="h6" gutterBottom>
        {currentBalance.value !== null ? (
          `${currentBalance.value} ${currentBalance.currency}`
        ) : (
          <CircularProgress size={24} />
        )}
      </Typography>
    );
  };

  const renderSectionContent = () => {
    switch (activeSection) {
      case "send":
        return <SendSection />;
      case "receive":
        return <ReceiveSection />;
      case "swap":
        return <SwapSection />;
      case "history":
        return <HistorySection />;
      case "security":
        return <SecuritySection />;
      case "donate":
        return <DonationsSection />;
      case "cards":
        return <CardsSection />;
      case "create":
        return (
          <WalletCreator
            onCreateWallet={function (address: string): void {
              dispatch(setWalletAddress(address));
              onClose();
            }}
          />
        );
      default:
        return (
          <>
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Avatar sx={{ width: 64, height: 64, mx: "auto", mb: 2 }}>
                <WalletIcon fontSize="large" />
              </Avatar>

              {/* Balance Type Selector */}
              <ToggleButtonGroup
                value={balanceType}
                exclusive
                onChange={handleBalanceTypeChange}
                aria-label="balance type"
                sx={{ mb: 2 }}
              >
                <ToggleButton value="crypto" aria-label="crypto">
                  <WalletIcon fontSize="small" sx={{ mr: 1 }} />
                  MOVEUSD
                </ToggleButton>
                <ToggleButton value="bank" aria-label="bank">
                  <FiatIcon fontSize="small" sx={{ mr: 1 }} />
                  Bank
                </ToggleButton>
                <ToggleButton value="rewards" aria-label="rewards">
                  <RewardsIcon fontSize="small" sx={{ mr: 1 }} />
                  Rewards
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Display selected balance */}
              {renderBalance()}

              <Chip
                label={
                  copied ? (
                    <>
                      <CheckIcon fontSize="small" sx={{ mr: 1 }} />
                      Copied!
                    </>
                  ) : (
                    <>
                      {shortenAddress(user?.walletAddress || "")}
                      <CopyIcon fontSize="small" sx={{ ml: 1 }} />
                    </>
                  )
                }
                onClick={handleCopyAddress}
                sx={{ cursor: "pointer" }}
              />
            </Box>
            <Divider />
            <List>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => dispatch(setWalletSection("send"))}
                >
                  <ListItemIcon>
                    <SendIcon />
                  </ListItemIcon>
                  <ListItemText primary="Send" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => dispatch(setWalletSection("receive"))}
                >
                  <ListItemIcon>
                    <QrCodeIcon />
                  </ListItemIcon>
                  <ListItemText primary="Receive" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => dispatch(setWalletSection("swap"))}
                >
                  <ListItemIcon>
                    <SwapIcon />
                  </ListItemIcon>
                  <ListItemText primary="Swap" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => dispatch(setWalletSection("cards"))}
                >
                  <ListItemIcon>
                    <CardIcon />
                  </ListItemIcon>
                  <ListItemText primary="Cards" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={async () => {
                    console.log(
                      "Fetching transactions for wallet:",
                      user?.walletAddress
                    );
                    await dispatch(
                      fetchTransactions({
                        accountAddress: user?.walletAddress || "",
                      })
                    );
                    dispatch(setWalletSection("history"));
                  }}
                >
                  <ListItemIcon>
                    <HistoryIcon />
                  </ListItemIcon>
                  <ListItemText primary="Transaction History" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => dispatch(setWalletSection("security"))}
                >
                  <ListItemIcon>
                    <SecurityIcon />
                  </ListItemIcon>
                  <ListItemText primary="Security & Privacy" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => dispatch(setWalletSection("donate"))}
                >
                  <ListItemIcon>
                    <DonationsIcon />
                  </ListItemIcon>
                  <ListItemText primary="Donations" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={onDisconnect}>
                  <ListItemIcon>
                    <SecurityIcon color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Disconnect Wallet"
                    primaryTypographyProps={{ color: "error" }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </>
        );
    }
  };

  const handleBack = () => {
    dispatch(setWalletSection("main"));
  };

  const getSectionTitle = () => {
    switch (activeSection) {
      case "send":
        return "Send Assets";
      case "receive":
        return "Receive Assets";
      case "swap":
        return "Swap Tokens";
      case "history":
        return "Transaction History";
      case "security":
        return "Security Settings";
      case "donate":
        return "Donations";
      default:
        return "Wallet";
    }
  };

  return (
    <SwipeableDrawer
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      anchor="right"
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: 400 },
          boxSizing: "border-box",
        },
      }}
    >
      <Box sx={{ width: "100%" }}>
        {activeSection !== "main" ? (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                p: 2,
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              }}
            >
              <IconButton onClick={handleBack} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6">{getSectionTitle()}</Typography>
            </Box>
            <Box sx={{ p: 2, width: "100%" }}>{renderSectionContent()}</Box>
          </>
        ) : (
          renderSectionContent()
        )}
      </Box>
    </SwipeableDrawer>
  );
};

const SwapSection = () => {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Swap Tokens
      </Typography>
      <Typography>
        This section would contain a token swap interface.
      </Typography>
    </Box>
  );
};

const SecuritySection = () => {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Security Settings
      </Typography>
      <Typography>
        This section would contain wallet security options.
      </Typography>
    </Box>
  );
};

const DonationsSection = () => {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Charitable Donations
      </Typography>
      <Typography>
        This section would contain Charitable Donations options.
      </Typography>
    </Box>
  );
};

export default WalletDialog;
